import React from "react";
import { createPortal } from "react-dom";
import Button from "./Button";
import {
  Dialog as BaseDialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "./Dialog";

/**
 * Modal component for displaying dialogs
 * This component is now a wrapper around the Dialog component for better consistency.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {React.ReactNode} props.footer - Modal footer
 * @param {string} props.size - Modal size (sm, md, lg, xl, full)
 * @param {boolean} props.closeOnClickOutside - Whether to close the modal when clicking outside
 * @param {string} props.className - Additional classes
 */
const Modal = React.memo(
  ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = "md",
    closeOnClickOutside = true,
    className = "",
    ...props
  }) => {
    // Size classes
    const sizeClasses = {
      sm: "max-w-md",
      md: "max-w-lg",
      lg: "max-w-2xl",
      xl: "max-w-4xl",
      full: "max-w-full mx-4",
    };

    return createPortal(
      <BaseDialog
        open={isOpen}
        onOpenChange={(open) => closeOnClickOutside && !open && onClose()}
        className={`${sizeClasses[size]} ${className}`}
        {...props}
      >
        {title && (
          <DialogHeader onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}

        <DialogContent>{children}</DialogContent>

        {footer && <DialogFooter>{footer}</DialogFooter>}
      </BaseDialog>,
      document.body,
    );
  },
);

Modal.displayName = "Modal";

/**
 * Modal.Footer component for standard modal actions
 * @param {Object} props
 * @param {Function} props.onCancel - Function to call when cancel button is clicked
 * @param {Function} props.onConfirm - Function to call when confirm button is clicked
 * @param {string} props.cancelText - Text for cancel button
 * @param {string} props.confirmText - Text for confirm button
 * @param {boolean} props.loading - Whether the confirm button is in loading state
 * @param {string} props.className - Additional classes
 */
Modal.Footer = React.memo(
  ({
    onCancel,
    onConfirm,
    cancelText = "Cancel",
    confirmText = "Confirm",
    loading = false, // Keep loading prop for internal logic
    className = "",
    confirmVariant = "primary", // Allow customizing confirm button variant
    confirmDisabled = false,
    cancelClassName = "", // Custom class for cancel button
    confirmClassName = "", // Custom class for confirm button
    ...props
  }) => {
    return (
      <div className={`flex justify-end space-x-3 ${className}`} {...props}>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className={cancelClassName}
        >
          {cancelText}
        </Button>
        <Button
          variant={confirmVariant} // Use the new prop
          onClick={onConfirm}
          // Remove the loading prop pass-through
          disabled={loading || confirmDisabled}
          className={confirmClassName}
        >
          {confirmText}
        </Button>
      </div>
    );
  },
);

Modal.Footer.displayName = "Modal.Footer";

export default Modal;
